<template>
  <div class="tw:px-4 tw:w-full tw:relative">
    <ConfirmDialog v-if="isRemovePopupOpen" @onConfirm="confirmLeaveOrganization" @onCancel="cancelLeaveOrganisation" />
    <OrganisationsViewLoadingSkeleton v-if="isRemovingUserFromOrganisationLoading || isLoadingCoreData" />
    <div v-else class="tw:pb-10 tw:py-4 tw:min-h-screen-minus-footer tw:max-w-4xl tw:mx-auto">
      <div>
        <router-link to="/" class="tw:text-blue-800 tw:hover:text-blue-800 tw:hover:cursor-pointer tw:font-bold"
          >← {{ $t('setup_back') }}
        </router-link>
      </div>
      <h1 class="tw:text-3xl tw:font-bold tw:mb-6 tw:mt-8">
        {{ $t('pages.organizationSwitcher.heading.mainHeading') }}
      </h1>
      <div class="tw:mb-10">
        <h2 class="tw:font-bold tw:mb-4">
          {{ $t('pages.organizationSwitcher.heading.selectedOrganization') }}
        </h2>
        <OrganisationCard v-if="selectedOrganisation" selected :organisation-details="selectedOrganisation" />
      </div>
      <div v-if="organisations?.length > 1" class="tw:flex tw:flex-col tw:gap-3">
        <h2 class="tw:font-bold tw:mb-1">{{ $t('pages.organizationSwitcher.heading.otherOrganizations') }}</h2>
        <template v-for="organisation in organisations">
          <OrganisationCard
            v-if="organisation.id !== selectedOrganisation.id"
            :key="organisation.id"
            :organisation-details="organisation"
            @onOrganisationSelectClick="changeOrganisation"
            @onOrganisationRemoveClick="removeOrganisation"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useStore } from 'vuex'

import OrganisationCard from '@/components/organisations/OrganisationCard.vue'
import ConfirmDialog from '@/components/base/ConfirmDialog.vue'
import OrganisationsViewLoadingSkeleton from '@/components/loadingSkeletons/OrganisationsViewLoadingSkeleton.vue'
import useAccommodations from '@/composables/useAccommodations'
import { useOrganisationsMutation } from '@/components/organisations/queries'
import type { Organisation } from '@/types'

const store = useStore()
/** Whether the remove confirmation dialog is open */
const isRemovePopupOpen = ref<boolean>(false)

/** Organisation pending removal (null when no removal in progress) */
const organisationToLeave = ref<Organisation | null>(null)

// ==================== Store Computed ====================

/** All organisations for current user */
const organisations = computed<Organisation[]>(() => store.state.session.organisations)

/** Core data loading state */
const isLoadingCoreData = computed<boolean>(() => store.state.session.isLoading)

/** Currently selected organisation */
const selectedOrganisation = computed<Organisation | null>(() => store.state.session.selectedOrganisation)

// ==================== Composables ====================

const { switchOrganisation } = useAccommodations()
const { removeUserFromOrganisation, isRemovingUserFromOrganisationLoading } = useOrganisationsMutation()

// ==================== Functions ====================

/**
 * Switch to a different organisation
 * @param newOrganisation - Organisation to switch to
 */
const changeOrganisation = (newOrganisation: Organisation): void => {
  switchOrganisation(newOrganisation)
}

/**
 * Open confirmation dialog to remove organisation
 * @param organisationToRemove - Organisation to remove
 */
const removeOrganisation = (organisationToRemove: Organisation): void => {
  organisationToLeave.value = organisationToRemove
  isRemovePopupOpen.value = true
}

/**
 * Cancel the organisation removal process
 */
const cancelLeaveOrganisation = (): void => {
  isRemovePopupOpen.value = false
  organisationToLeave.value = null
}

/**
 * Confirm and execute organisation removal
 */
const confirmLeaveOrganization = async (): Promise<void> => {
  if (organisationToLeave.value?.id) {
    await removeUserFromOrganisation({ organisationId: organisationToLeave.value.id })
  }
  isRemovePopupOpen.value = false
  organisationToLeave.value = null
}
</script>

<style scoped>
.back {
  color: var(--tw-color-blue-800);
}
.back:hover {
  color: var(--tw-color-blue-800);
}
</style>
